import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const WITHDRAWAL_REASONS = [
  { value: "team_unable_to_attend", label: "Team unable to attend" },
  { value: "roster_changes", label: "Roster changes" },
  { value: "medical", label: "Medical" },
  { value: "travel_issues", label: "Travel issues" },
  { value: "duplicate_registration", label: "Duplicate registration" },
  { value: "wrong_division", label: "Wrong division" },
  { value: "other", label: "Other" },
];

type Props = {
  /** One of these identifies the row. */
  registrationId?: string | null;
  trackingId?: string | null;
  teamName?: string;
  /** Already withdrawn rows offer the reverse action. */
  withdrawn: boolean;
  /** What the team has paid — caps any refund. */
  amountPaid?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged?: () => void;
};

const money = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

/**
 * Withdrawing keeps the team on the sheet: the status flips to dropped (or
 * refunded when money goes back), a withdrawal record is written, and any
 * refund posts a negative ledger entry so the books still reconcile.
 */
export default function WithdrawTeamDialog({
  registrationId,
  trackingId,
  teamName,
  withdrawn,
  amountPaid = 0,
  open,
  onOpenChange,
  onChanged,
}: Props) {
  const [reason, setReason] = useState("team_unable_to_attend");
  const [details, setDetails] = useState("");
  const [resolution, setResolution] = useState<"none" | "refund" | "credit">("none");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const tracked = !registrationId && !!trackingId;

  useEffect(() => {
    if (!open) return;
    setReason("team_unable_to_attend");
    setDetails("");
    setResolution("none");
    setAmount(amountPaid ? String(amountPaid.toFixed(2)) : "");
  }, [open, registrationId, trackingId, amountPaid]);

  const submit = async () => {
    if (!details.trim()) {
      toast.error("A reason is required");
      return;
    }
    const value = Number(amount || 0);
    if (!withdrawn && resolution !== "none") {
      if (!Number.isFinite(value) || value <= 0) {
        toast.error("Enter an amount greater than zero");
        return;
      }
      if (resolution === "refund" && value > amountPaid + 0.001) {
        toast.error(`A refund can't exceed what was paid (${money(amountPaid)})`);
        return;
      }
    }

    setBusy(true);
    const { data, error } = await supabase.functions.invoke("withdraw-registration", {
      body: {
        action: withdrawn ? "restore" : "withdraw",
        registration_id: registrationId ?? undefined,
        tracking_id: trackingId ?? undefined,
        reason,
        reason_details: details.trim(),
        refund_amount: !withdrawn && resolution === "refund" ? value : 0,
        credit_amount: !withdrawn && resolution === "credit" ? value : 0,
      },
    });
    setBusy(false);
    const err = error?.message || (data as any)?.error;
    if (err) {
      toast.error(err);
      return;
    }
    toast.success(withdrawn ? "Team restored" : "Team withdrawn");
    onChanged?.();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{withdrawn ? "Restore team" : "Withdraw team"}</DialogTitle>
          <DialogDescription>
            {withdrawn ? (
              <>
                {teamName || "This team"} goes back to registered and counts towards scheduling
                again. Any refund posted at withdrawal is voided in the ledger, never deleted.
              </>
            ) : (
              <>
                {teamName || "This team"} stays visible on the sheet, marked withdrawn, but is
                excluded from scheduling and the active counts.
                {!tracked && amountPaid > 0 ? ` They have paid ${money(amountPaid)}.` : ""}
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!withdrawn && (
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WITHDRAWAL_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="withdraw-details">
              {withdrawn ? "Why is this being reversed?" : "Details"}
            </Label>
            <Textarea
              id="withdraw-details"
              rows={3}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={
                withdrawn ? "e.g. Coach confirmed they can attend after all" : "What happened?"
              }
            />
          </div>

          {!withdrawn && !tracked && (
            <div className="space-y-2">
              <Label>Refund or credit</Label>
              <Select value={resolution} onValueChange={(v) => setResolution(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No refund or credit</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                  <SelectItem value="credit">Credit for a future event</SelectItem>
                </SelectContent>
              </Select>
              {resolution !== "none" && (
                <div className="space-y-1">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-muted-foreground">
                    {resolution === "refund"
                      ? `Records the refund as owed — nothing is sent automatically. Post it yourself, then mark it sent in the Payments dialog. Maximum ${money(
                          amountPaid
                        )}.`
                      : "Recorded as agreed, not issued: credits aren't redeemable at checkout yet, so you honour this manually on their next registration."}
                  </p>

                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !details.trim()}
            variant={withdrawn ? "default" : "destructive"}
          >
            {busy ? "Working…" : withdrawn ? "Restore team" : "Withdraw team"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
